import { Typography, Box, Container, Paper, Divider, Button } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import SuccessIcon from '@acorn-portal/shared/src/assets/shared/icons/circled-check-green.svg?react';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import HomeIcon from '@mui/icons-material/Home';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import {
	GetPaymentCollectionOptionsResponseModel,
	GetPaymentDetailsResponseModel,
	SplitPaymentPlanModel,
} from '@acorn-portal/shared/src/services/api';
import PaymentScheduleTable from '@acorn-portal/shared/src/components/display/payment-schedule-table';
import FlagLanderBanner from '@acorn-portal/shared/src/components/banner/FlagLanderBanner';

interface IState {
	paymentDetails: GetPaymentDetailsResponseModel;
	installmentPlan: GetPaymentCollectionOptionsResponseModel | SplitPaymentPlanModel[];
	amount: number;
	isPayInFull: boolean;
}
const PaymentConfirmation: FC = () => {
	const payNowDetails = useAppSelector((redux) => redux.payNow);
	const navigate = useNavigate();
	const [state, setState] = useState<IState | undefined>();

	useEffect(() => {
		if (!payNowDetails.details) {
			navigate(ROUTE_PATHS.home);
		} else {
			setState({
				paymentDetails: payNowDetails.details,
				installmentPlan: payNowDetails.missingPaymentFormDetails
					? (payNowDetails.collectionOptions.find(
							(x) => x.paymentPlanType === payNowDetails.missingPaymentFormDetails.splitPaymentPlanType,
						) as GetPaymentCollectionOptionsResponseModel)
					: payNowDetails.details.splitPaymentPlans,
				amount: payNowDetails.missingPaymentFormDetails
					? (payNowDetails.collectionOptions.find(
							(x) => x.paymentPlanType === payNowDetails.missingPaymentFormDetails.splitPaymentPlanType,
						)?.installments[0].amount as number)
					: payNowDetails.details.overDuePaymentPlan?.amount || 0,
				isPayInFull:
					payNowDetails.missingPaymentFormDetails &&
					payNowDetails.missingPaymentFormDetails.splitPaymentPlanType === 'PayInFull',
			});
		}
	}, [payNowDetails]);

	const { details } = payNowDetails;

	return (
		<Paper
			sx={{
				margin: 1.5,
				borderRadius: '8px',
				paddingBottom: 3,
			}}
		>
			<FlagLanderBanner
				text={
					<Box
						sx={(theme) => ({
							[theme.breakpoints.down('md')]: { fontSize: '1.4rem' },
							[theme.breakpoints.down('sm')]: {
								fontSize: '1.1rem',
							},
							width: '80%',
						})}
					>
						Thank you for your payment!
					</Box>
				}
			/>{' '}
			<Container maxWidth='sm' sx={{ textAlign: 'center' }}>
				<Box my={4}>
					<Typography variant='h6' color='secondary.main' paddingBottom={1}>
						Your payment of {state?.amount ? formatCurrency(state?.amount) : ''} has been taken.
					</Typography>
					<Box
						sx={{
							'& svg': {
								maxHeight: '120px',
								margin: '1.2rem',
								'.cls-1': {
									fill: '#2e7d32',
								},
							},
						}}
					>
						<SuccessIcon />
					</Box>
					<Typography align='left'>
						Your policy payments are now up to date. Please rest assured that your cover will now continue as normal.
					</Typography>
					<Typography align='left' sx={{ fontWeight: 600, marginTop: 1 }}>
						A receipt for this transaction has been sent via email for your records.
					</Typography>
					{state?.installmentPlan && !state.isPayInFull && (
						<>
							<br />
							<Divider sx={{ borderColor: 'rgb(109, 109, 109)' }} />

							{details?.splitPaymentPlans && details.splitPaymentPlans.length > 0 ? (
								<>
									<Typography variant='h6' component='h6' color='secondary.main' sx={{ textAlign: 'left', my: 2 }}>
										Your Instalment Plan
									</Typography>
									<PaymentScheduleTable splitPaymentPlans={details.splitPaymentPlans} />
								</>
							) : null}
						</>
					)}
					<Button
						variant='contained'
						fullWidth
						startIcon={<HomeIcon />}
						sx={{ my: 4 }}
						onClick={() => navigate(ROUTE_PATHS.home)}
					>
						Go Home
					</Button>
				</Box>
			</Container>
		</Paper>
	);
};

export default PaymentConfirmation;
